/* eslint import/no-webpack-loader-syntax: off */
import { useCallback, useEffect, useRef } from "react";
import mapboxgl from '!mapbox-gl';

import { useMapLayersV2 } from "../../../../../../Map/MapLayersV2Context";
import { viewImageState } from "../../ViewImageState";
import { useViewImageContext } from "../../ViewImageContext";
import { useViewLandContext } from "../../../ViewLandContext";
import { formatFixedNumber, formatMeters } from "../../../../../../../../shared/numberFormat";
import * as LandService from "../../../../../services/LandService";


const PopupEvent = () => {
    const mapLayers = useMapLayersV2();
    const viewImageContext = useViewImageContext();
    const viewLandContext = useViewLandContext();
    const popupsRef = useRef([]);
    const popupGenerationRef = useRef(0);
    const currentLandIdRef = useRef(null);
    const activeSectionRef = useRef(viewLandContext?.activeSection);

    const clearPopups = useCallback(() => {
        popupGenerationRef.current += 1;
        popupsRef.current.forEach((popup) => popup.remove());
        popupsRef.current = [];
    }, []);

    const isRenderedImageLayerVisible = (map, landId, imageType) => {
        const layerId = `${imageType}-${landId}`;

        if (!map.getLayer(layerId)) {
            return false;
        }

        return map.getLayoutProperty(layerId, "visibility") !== "none";
    };

    const getAltimetryHeightAt = (landId, {lng, lat}) => {
        const raster = viewImageState.getLandAltimetry(landId);

        if (!raster?.heights || !raster?.bbox || !raster?.width || !raster?.height) {
            return null;
        }

        const west = raster.bbox[0]?.[0];
        const north = raster.bbox[0]?.[1];
        const east = raster.bbox[1]?.[0];
        const south = raster.bbox[2]?.[1];

        if (![west, north, east, south].every(Number.isFinite) || east === west || north === south) {
            return null;
        }

        if (lng < west || lng > east || lat < south || lat > north) {
            return null;
        }

        const column = Math.min(
            raster.width - 1,
            Math.max(0, Math.floor(((lng - west) / (east - west)) * raster.width))
        );
        const row = Math.min(
            raster.height - 1,
            Math.max(0, Math.floor(((north - lat) / (north - south)) * raster.height))
        );

        return formatMeters(raster.heights[row * raster.width + column]);
    };

    const getYieldMapValueAt = (landId, {lng, lat}) => {
        const raster = viewImageState.getLandYieldMap(landId);

        if (!raster?.values || !raster?.bbox || !raster?.width || !raster?.height) {
            return null;
        }

        const west = raster.bbox[0]?.[0];
        const north = raster.bbox[0]?.[1];
        const east = raster.bbox[1]?.[0];
        const south = raster.bbox[2]?.[1];

        if (![west, north, east, south].every(Number.isFinite) || east === west || north === south) {
            return null;
        }

        if (lng < west || lng > east || lat < south || lat > north) {
            return null;
        }

        const column = Math.min(
            raster.width - 1,
            Math.max(0, Math.floor(((lng - west) / (east - west)) * raster.width))
        );
        const row = Math.min(
            raster.height - 1,
            Math.max(0, Math.floor(((north - lat) / (north - south)) * raster.height))
        );
        const value = raster.values?.[row]?.[column];

        if (value == null || !Number.isFinite(Number(value))) {
            return null;
        }

        return `${formatFixedNumber(value)} t/ha`;
    };

    useEffect(() => {
        const nextLandId = viewLandContext?.land?.land_id || null;

        if (currentLandIdRef.current && currentLandIdRef.current !== nextLandId) {
            clearPopups();
        }

        currentLandIdRef.current = nextLandId;
    }, [clearPopups, viewLandContext?.land?.land_id]);

    useEffect(() => {
        activeSectionRef.current = viewLandContext?.activeSection;

        if (viewLandContext?.activeSection !== "images") {
            clearPopups();
        }
    }, [clearPopups, viewLandContext?.activeSection]);

    // Events
    const loadPopup = (map, land, e) => {
        const { lat, lng } = e.lngLat;
        const generation = popupGenerationRef.current;

        const image = viewImageState.getLandTiff(land)
        if (!image) {
            return;
        }

        const bbox = image.getBoundingBox();
        const pixelWidth = image.getWidth();
        const pixelHeight = image.getHeight();
        const bboxWidth = bbox[ 2 ] - bbox[ 0 ];
        const bboxHeight = bbox[ 3 ] - bbox[ 1 ];
        const widthPct = ( lng - bbox[ 0 ] ) / bboxWidth;
        const heightPct = ( lat - bbox[ 1 ] ) / bboxHeight;
        const xPx = Math.floor( pixelWidth * widthPct );
        const yPx = Math.floor( pixelHeight * ( 1 - heightPct ) );

        if (!Number.isFinite(xPx) || !Number.isFinite(yPx) ||
            xPx < 0 || yPx < 0 || xPx >= pixelWidth || yPx >= pixelHeight) {
            return;
        }

        const window = [ xPx, yPx, xPx + 1, yPx + 1 ];
        image.readRasters( {window} ).then(res => {
            if (generation !== popupGenerationRef.current) {
                return;
            }

            const imageLand = viewImageState.getLand(land);
            if (!imageLand || !isRenderedImageLayerVisible(map, land, imageLand.imageType)) {
                return;
            }

            const value = Number(res?.[0]?.[0]);
            if (!Number.isFinite(value)) {
                return;
            }

            const popup = new mapboxgl.Popup()
                .setLngLat([lng, lat])
                .setHTML(formatFixedNumber(value))
                .addTo(map);

            popupsRef.current = [...popupsRef.current, popup];
        }).catch(() => {});
    };

    const loadAltimetryPopup = (map, landId, e) => {
        const { lat, lng } = e.lngLat;
        const heightLabel = getAltimetryHeightAt(landId, {lat, lng});

        if (!heightLabel) {
            return;
        }

        const popup = new mapboxgl.Popup()
            .setLngLat([lng, lat])
            .setHTML(heightLabel)
            .addTo(map);

        popupsRef.current = [...popupsRef.current, popup];
    };

    const loadHdIndexPopup = (map, landId, imageLand, e) => {
        const acquisitionId = imageLand.selectedAcquisitionId;
        if (!acquisitionId) return;
        const layer = imageLand.imageType.replace(/_hd$/, "");
        const {lng, lat} = e.lngLat;
        const generation = popupGenerationRef.current;
        LandService.getHdAcquisitionSample(landId, acquisitionId, layer, {lng, lat})
            .then((response) => {
                const current = viewImageState.getLand(landId);
                const value = Number(response?.data?.value);
                if (
                    generation !== popupGenerationRef.current ||
                    !response?.ok || response.data?.value == null || !Number.isFinite(value) ||
                    current?.imageType !== imageLand.imageType ||
                    current?.selectedAcquisitionId !== acquisitionId ||
                    !isRenderedImageLayerVisible(map, landId, imageLand.imageType)
                ) return;
                const popup = new mapboxgl.Popup()
                    .setLngLat([lng, lat])
                    .setHTML(formatFixedNumber(value))
                    .addTo(map);
                popupsRef.current = [...popupsRef.current, popup];
            })
            .catch(() => {});
    };

    const loadYieldMapPopup = (map, landId, e) => {
        const { lat, lng } = e.lngLat;
        const yieldLabel = getYieldMapValueAt(landId, {lat, lng});

        if (!yieldLabel) {
            return;
        }

        const popup = new mapboxgl.Popup()
            .setLngLat([lng, lat])
            .setHTML(yieldLabel)
            .addTo(map);

        popupsRef.current = [...popupsRef.current, popup];
    };

    useEffect(() => {
        mapLayers.on('click', (map) => { 
            return (e) => { 
                if (activeSectionRef.current !== "images") {
                    return;
                }

                const features = map.queryRenderedFeatures(e.point, { layers: ["land"] });
                if (features.length >= 1 && features[0].properties.resource_type == "land") {
                    const landId = features[0].properties.id;
                    const currentLandId = currentLandIdRef.current;
                    if (!currentLandId || landId !== currentLandId) {
                        return;
                    }

                    const imageLand = viewImageState.getLand(landId);

                    if (!imageLand) {
                        return;
                    }

                    if (["evi", "ndvi", "evi_hd", "ndvi_hd", "ndre_hd", "altimetry", "yield_map"].indexOf(imageLand.imageType) === -1) {
                        return; 
                    }

                    if (!isRenderedImageLayerVisible(map, landId, imageLand.imageType)) {
                        return;
                    }

                    if (imageLand.imageType === "altimetry") {
                        loadAltimetryPopup(map, landId, e);
                        return;
                    }

                    if (imageLand.imageType === "yield_map") {
                        loadYieldMapPopup(map, landId, e);
                        return;
                    }

                    if (imageLand.imageType.endsWith("_hd")) {
                        loadHdIndexPopup(map, landId, imageLand, e);
                        return;
                    }

                    loadPopup(map, landId, e);
                }
            };
        });
    }, []);

    useEffect(() => {
        if (viewImageContext.clearMapVersion < 1) {
            return;
        }

        clearPopups();
    }, [clearPopups, viewImageContext.clearMapVersion]);

    useEffect(() => {
        return () => {
            clearPopups();
        };
    }, [clearPopups]);

    return (
        <>
        </>
    );
}

export default PopupEvent;
